import { getPrettyNumber } from './formatting';

export const DISCOVER_PAGE_SIZE = 25;

export const SEGMENT_OPTIONS = [
  { value: 'loyal_advocate',             label: 'Loyal Advocate' },
  { value: 'cross_category_opportunity', label: 'Crossover Candidate' },
  { value: 'pure_discovery',             label: 'Pure Discovery' },
];

export const DISCOVER_SORT_OPTIONS = [
  {
    label: 'General',
    options: [{ value: 'score', label: 'Creator Score', sublabel: 'Based on ability to drive sales, responsiveness and more.' }]
  },
  {
    label: 'Social Following',
    options: [
      { value: 'followers', label: 'Total Followers', sublabel: 'Total followers across all social platforms.' },
      { value: 'followers-instagram', label: 'Instagram Followers' },
      { value: 'followers-tiktok', label: 'TikTok Followers' },
      { value: 'followers-youtube', label: 'YouTube Followers' }
    ]
  },
  {
    label: 'Performance',
    options: [
      { value: 'volume', label: 'Peak Monthly Sales', sublabel: 'Highest single-month sales in the last 6 months.' },
      { value: 'traffic', label: 'Traffic Generated', sublabel: 'Total clicks driven to brand/retailer websites.' },
      { value: 'orders', label: 'No. of Orders', sublabel: 'Total orders to-date.' },
      { value: 'aov', label: 'AOV', sublabel: 'Average Order Value across all purchases.' },
      { value: 'gmv', label: 'Total Sales', sublabel: 'All-time sales since joining ShopMy.' },
      { value: 'gmv-monthly', label: 'Monthly GMV', sublabel: 'GMV past 30 days.' },
      { value: 'gmv-weekly', label: 'Weekly GMV', sublabel: 'GMV past 7 days.' },
      { value: 'opportunities-roi', label: 'Opportunities ROI', sublabel: 'Return on investment for Opportunities.' }
    ]
  },
  {
    label: 'Gifting',
    options: [
      { value: 'gifting-acceptance', label: 'Gifting Acceptance', sublabel: 'Likelihood of accepting gifting.' },
      { value: 'gifting-promotion', label: 'Gifting Promotion', sublabel: 'Likelihood of promoting after receiving gifting.' },
      { value: 'gifting-volume', label: 'Gifting Volume', sublabel: 'Volume driven as a result of gifting.' }
    ]
  },
  {
    label: 'Discount Codes',
    options: [
      { value: 'code-promotion', label: 'Code Promotion', sublabel: 'Percentage of codes promoted.' },
      { value: 'code-volume', label: 'Code Volume', sublabel: 'Volume driven after receiving code.' }
    ]
  },
  {
    label: 'Advanced',
    options: [
      { value: 'affinity', label: 'Brand Match Score', sublabel: "Match between a creator's taste and your brand." }
    ]
  }
];


const dollars = v => `$${getPrettyNumber(v || 0)}`;
const count   = v => getPrettyNumber(v || 0);

// Returns { label, value } for the metric the user is currently sorting by,
// or null if no metric should be shown (Brand Match Score is already on the card; ShopMy
// score is too opaque to be useful).
export const getSortMetric = (sortBy, c) => {
  switch (sortBy) {
    case 'followers':           return { label: 'Total Followers',      value: count((c.instagramCount || 0) + (c.youtubeCount || 0) + (c.tiktokCount || 0)) };
    case 'followers-instagram': return { label: 'Instagram Followers',  value: count(c.instagramCount) };
    case 'followers-tiktok':    return { label: 'TikTok Followers',     value: count(c.tiktokCount) };
    case 'followers-youtube':   return { label: 'YouTube Followers',    value: count(c.youtubeCount) };

    case 'gmv':                 return { label: 'Total Sales',          value: dollars(c.totalVolume) };
    case 'gmv-monthly':         return { label: 'Monthly Sales',        value: dollars(c.monthlyVolume) };
    case 'gmv-weekly':          return { label: 'Weekly Sales',         value: dollars(c.weeklyVolume) };
    case 'volume':              return { label: 'Peak Monthly Sales',   value: dollars(c.peakMonthlyVolume) };
    case 'orders':              return { label: 'Total Orders',         value: count(c.totalOrders) };
    case 'aov':                 return { label: 'Avg Order Value',      value: dollars(c.averageOrderAmount) };
    case 'traffic':             return { label: 'Monthly Clicks',       value: count(c.monthlyPinClicks) };
    case 'opportunities-roi':   return { label: 'Opportunities ROI / Link', value: dollars(c.opportunitiesROIPerLink) };
    case 'gifting-volume':      return { label: 'Gifting Volume',       value: dollars(c.giftingOrderVolume) };
    case 'code-volume':         return { label: 'Code Volume',          value: dollars(c.codesOrderVolume) };

    // Deliberately skipped: 'affinity' (Brand Match Score already shown), 'score' (opaque),
    // and the rate sorts (gifting-acceptance, gifting-promotion, code-promotion).
    default: return null;
  }
};

// A vibe hit is a "partial match" when the query left criteria unmatched
// (evidence contract v3). Full match ⇒ `unmatched` empty. Used to optionally hide
// partial matches in Discover. Keyed off `unmatched`, not `label`, so it's robust
// to label-taxonomy changes; missing/non-array `unmatched` ⇒ not partial.
export const hasUnmatchedCriteria = evidence =>
  Array.isArray(evidence?.unmatched) && evidence.unmatched.length > 0;

// Builds the ordered "why surfaced" clause parts for a vibe hit (caller joins with
// ' · '). Per-hit clauses from `evidence`; set-level geo/taste from `disambiguation`.
// Never emits machine internals (label/scoring/tag-ids).
export const composeVibeWhy = (evidence, disambiguation) => {
  // Degraded = query split failed → facet arrays empty, so emit the generic line.
  if (disambiguation?.degraded === true) return ['matches your search'];

  const ev = evidence || {};
  const matched = Array.isArray(ev.matched) ? ev.matched : [];
  const unmatched = Array.isArray(ev.unmatched) ? ev.unmatched : [];
  const excluded = Array.isArray(ev.excluded) ? ev.excluded : [];
  const parts = [];

  // Affirmative clauses (endpoint order).
  for (const m of matched) {
    const value = (m?.value || '').trim();
    if (value) parts.push(value);
  }
  // Unmatched criteria → "not X".
  for (const u of unmatched) {
    const value = (u?.value || '').trim();
    if (value) parts.push(`not ${value}`);
  }
  // Negated tags the creator holds.
  for (const x of excluded) {
    const value = (x?.value || '').trim();
    if (value) parts.push(`excluded as ${value}`);
  }

  // Set-level taste clause (present only with results). Geo is NOT taken from
  // disambiguation.geo_tags — that's the query constraint; the creator's own
  // location is a per-hit matched {role:"filter"} entry (evidence contract v4),
  // already rendered above. geo_tags is for a once-per-search header, not the line.
  if (disambiguation) {
    switch (disambiguation.taste_source) {
      case 'what_phrase': {
        const phrase = (disambiguation.what_phrase || '').trim();
        if (phrase) parts.push(`matches ${phrase}`);
        break;
      }
      case 'whole_query':
        parts.push('matches your search');
        break;
      // 'synthesized_who' → omit; the who clause already covers it.
      default:
        break;
    }
  }

  return parts;
};

// Returns a footnote string when the active sort applies a hidden server-side
// filter that excludes creators (so users aren't surprised by missing rows).
// Returns null when no filter applies.
export const getSortFilterNote = (sortBy) => {
  switch (sortBy) {
    case 'aov':               return 'Showing only creators with 25+ orders';
    case 'opportunities-roi': return 'Showing only creators with 5+ completed opportunities';
    default: return null;
  }
};
